import {
  RESET_VALUE,
  computed,
  isUninitialized,
  unsafe__withoutCapture
} from "@tldraw/state";
import { isBinding, isPageId, isShape } from "@tldraw/tlschema";
import { objectMapValues } from "@tldraw/utils";
import { Box } from "../../../primitives/Box.mjs";
import { RBushIndex } from "./RBushIndex.mjs";
class SpatialIndexManager {
  constructor(editor) {
    this.editor = editor;
    this.rbush = new RBushIndex();
    this.spatialIndexComputed = this.createSpatialIndexComputed();
  }
  editor;
  rbush;
  spatialIndexComputed;
  lastPageId = null;
  // Bumps only when the rbush content may have changed. Consumers subscribe
  // via the computed; a stable epoch lets prop-only diffs skip downstream
  // invalidations.
  _boundsEpoch = 0;
  rebuildAndBumpEpoch() {
    this.buildFromScratch();
    this._boundsEpoch++;
    return this._boundsEpoch;
  }
  createSpatialIndexComputed() {
    const shapeHistory = this.editor.store.query.filterHistory("shape");
    const bindingHistory = this.editor.store.query.filterHistory("binding");
    return computed("spatialIndex", (_prevValue, lastComputedEpoch) => {
      const shapeDiff = shapeHistory.getDiffSince(lastComputedEpoch);
      const bindingDiff = bindingHistory.getDiffSince(lastComputedEpoch);
      const currentPageId = this.editor.getCurrentPageId();
      if (isUninitialized(_prevValue) || shapeDiff === RESET_VALUE || bindingDiff === RESET_VALUE || this.lastPageId !== currentPageId) {
        return unsafe__withoutCapture(() => this.rebuildAndBumpEpoch());
      }
      if (shapeDiff.length === 0 && bindingDiff.length === 0) return this._boundsEpoch;
      if (unsafe__withoutCapture(() => this.processIncrementalUpdate(shapeDiff, bindingDiff))) {
        this._boundsEpoch++;
      }
      return this._boundsEpoch;
    });
  }
  buildFromScratch() {
    this.rbush.clear();
    this.lastPageId = this.editor.getCurrentPageId();
    const elements = [];
    for (const shape of this.editor.getCurrentPageShapes()) {
      const bounds = this.editor.getShapePageBounds(shape.id);
      if (bounds && bounds.isValid()) {
        elements.push({
          minX: bounds.minX,
          minY: bounds.minY,
          maxX: bounds.maxX,
          maxY: bounds.maxY,
          id: shape.id
        });
      }
    }
    this.rbush.bulkLoad(elements);
  }
  processIncrementalUpdate(shapeDiff, bindingDiff) {
    const pendingRemoves = /* @__PURE__ */ new Set();
    const pendingUpserts = [];
    const transformChanged = /* @__PURE__ */ new Set();
    const dirty = /* @__PURE__ */ new Set();
    const queue = [];
    const markDirty = (id) => {
      if (!dirty.has(id)) {
        dirty.add(id);
        queue.push(id);
      }
    };
    for (const changes of shapeDiff) {
      for (const shape of objectMapValues(changes.added)) {
        if (!isShape(shape)) continue;
        transformChanged.add(shape.id);
        markDirty(shape.id);
      }
      for (const shape of objectMapValues(changes.removed)) {
        if (!isShape(shape)) continue;
        if (this.rbush.has(shape.id)) {
          pendingRemoves.add(shape.id);
        }
        if (!isPageId(shape.parentId)) markDirty(shape.parentId);
      }
      for (const [from, to] of objectMapValues(changes.updated)) {
        if (!isShape(to)) continue;
        transformChanged.add(to.id);
        markDirty(to.id);
        if (isShape(from) && from.parentId !== to.parentId && !isPageId(from.parentId)) {
          markDirty(from.parentId);
        }
      }
    }
    for (const changes of bindingDiff) {
      for (const binding of objectMapValues(changes.added)) {
        if (!isBinding(binding)) continue;
        markDirty(binding.fromId);
        markDirty(binding.toId);
      }
      for (const binding of objectMapValues(changes.removed)) {
        if (!isBinding(binding)) continue;
        markDirty(binding.fromId);
        markDirty(binding.toId);
      }
      for (const [from, to] of objectMapValues(changes.updated)) {
        if (!isBinding(to)) continue;
        markDirty(to.fromId);
        markDirty(to.toId);
        if (isBinding(from) && (from.fromId !== to.fromId || from.toId !== to.toId)) {
          markDirty(from.fromId);
          markDirty(from.toId);
        }
      }
    }
    const transformQueue = [...transformChanged];
    while (transformQueue.length) {
      const id = transformQueue.pop();
      for (const childId of this.editor.getSortedChildIdsForParent(id)) {
        if (!transformChanged.has(childId)) {
          transformChanged.add(childId);
          transformQueue.push(childId);
        }
        markDirty(childId);
      }
    }
    while (queue.length) {
      const id = queue.pop();
      const record = this.editor.store.unsafeGetWithoutCapture(id);
      if (record && isShape(record) && !isPageId(record.parentId)) {
        markDirty(record.parentId);
      }
      for (const binding of this.editor.getBindingsInvolvingShape(id)) {
        markDirty(binding.fromId);
        markDirty(binding.toId);
      }
    }
    for (const id of dirty) {
      const indexedElement = pendingRemoves.has(id) ? void 0 : this.rbush.getElement(id);
      const record = this.editor.store.unsafeGetWithoutCapture(id);
      if (!record || !isShape(record) || this.editor.getAncestorPageId(record) !== this.lastPageId) {
        if (this.rbush.has(id)) {
          pendingRemoves.add(id);
        }
        continue;
      }
      const bounds = this.editor.getShapePageBounds(id);
      if (bounds && bounds.isValid()) {
        if (!this.areBoundsEqualToSpatialElement(bounds, indexedElement)) {
          pendingRemoves.delete(id);
          pendingUpserts.push({
            minX: bounds.minX,
            minY: bounds.minY,
            maxX: bounds.maxX,
            maxY: bounds.maxY,
            id
          });
        }
      } else if (this.rbush.has(id)) {
        pendingRemoves.add(id);
      }
    }
    if (pendingRemoves.size === 0 && pendingUpserts.length === 0) return false;
    this.rbush.applyBatch(pendingRemoves, pendingUpserts);
    return true;
  }
  areBoundsEqualToSpatialElement(a, b) {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return a.minX === b.minX && a.minY === b.minY && a.maxX === b.maxX && a.maxY === b.maxY;
  }
  /**
   * Get shape IDs within the given bounds.
   * Optimized for viewport culling queries.
   *
   * Note: Results are unordered. If you need z-order, combine with sorted shapes:
   * ```ts
   * const candidates = editor.spatialIndex.getShapeIdsInsideBounds(bounds)
   * const sorted = editor.getCurrentPageShapesSorted().filter(s => candidates.has(s.id))
   * ```
   *
   * @param bounds - The bounds to search within
   * @returns Unordered set of shape IDs within the bounds
   *
   * @public
   */
  getShapeIdsInsideBounds(bounds) {
    this.spatialIndexComputed.get();
    return this.rbush.search(bounds);
  }
  /**
   * Get shape IDs at a point (with optional margin).
   * Creates a small bounding box around the point and searches the spatial index.
   *
   * Note: Results are unordered. If you need z-order, combine with sorted shapes:
   * ```ts
   * const candidates = editor.spatialIndex.getShapeIdsAtPoint(point, margin)
   * const sorted = editor.getCurrentPageShapesSorted().filter(s => candidates.has(s.id))
   * ```
   *
   * @param point - The point to search at
   * @param margin - The margin around the point to search (default: 0)
   * @returns Unordered set of shape IDs that could potentially contain the point
   *
   * @public
   */
  getShapeIdsAtPoint(point, margin = 0) {
    this.spatialIndexComputed.get();
    return this.rbush.search(new Box(point.x - margin, point.y - margin, margin * 2, margin * 2));
  }
  /**
   * Dispose of the spatial index manager.
   * Clears the R-tree to prevent memory leaks.
   *
   * @public
   */
  dispose() {
    this.rbush.dispose();
    this.lastPageId = null;
  }
}
export {
  SpatialIndexManager
};
//# sourceMappingURL=SpatialIndexManager.mjs.map
